import { commercialEditions, type CommercialEditionId } from '../../config/commercialPlans';
import { resolvedCloudSyncMode } from '../../config/runtime';
import { getAuthState } from '../auth/auth-store';
import { cloudConfigured, pullCloudSync, pushCloudSync, syncCatalogSnapshot } from '../production/cloud-api';
import { idbClear, idbDelete, idbGet, idbGetAll, idbPut, openOfflineDatabase } from './indexed-db';
import { decideDatasetReconciliation } from './sync-reconciliation';

export type SyncOperationStatus = 'pending' | 'syncing' | 'synced' | 'error' | 'conflict';
export type ConflictResolution = 'keep-local' | 'use-synced';

export type LocalDatasetRecord = {
  key: string;
  value: string;
  hash: string;
  revision: number;
  updatedAt: string;
};

export type SyncOperation = {
  id: string;
  entityKey: string;
  action: 'upsert' | 'delete';
  value: string | null;
  hash: string;
  baseRevision: number;
  clientRevision: number;
  status: SyncOperationStatus;
  retryCount: number;
  createdAt: string;
  updatedAt: string;
  syncedAt?: string;
  error?: string;
};

export type SyncConflict = {
  id: string;
  operationId: string;
  entityKey: string;
  localValue: string | null;
  syncedValue: string | null;
  localRevision: number;
  syncedRevision: number;
  detectedAt: string;
  status: 'open' | 'resolved-local' | 'resolved-synced';
  resolvedAt?: string;
};

type RemoteShadowRecord = { key: string; value: string | null; hash: string; revision: number; updatedAt: string };
type MetaRecord = { key: string; value: string | number | boolean | null };

export type OfflineState = {
  ready: boolean;
  connection: 'online' | 'offline' | 'local-only';
  editionId: CommercialEditionId;
  editionName: string;
  indexedDbAvailable: boolean;
  serviceWorkerReady: boolean;
  installAvailable: boolean;
  persistentStorage: boolean;
  storageUsedBytes: number;
  storageQuotaBytes: number;
  trackedDatasets: number;
  pendingOperations: number;
  syncedOperations: number;
  errorOperations: number;
  openConflicts: number;
  lastSyncAt?: string;
  queue: SyncOperation[];
  conflicts: SyncConflict[];
};

const CHANGE_EVENT = 'nexo:offline-state-changed';
const EDITION_KEY = 'nexo.commercial.edition';
const DEVICE_KEY = 'nexo.phase11.device-id';
const VALIDATION_KEY = 'nexo.phase11.validation.record';
const CATALOG_KEY = 'nexo.phase3.catalog.database';
const ignoredKeys = new Set(['nexo.theme', EDITION_KEY]);
const localOnlyDatasets = new Set(['nexo.phase2.local.database', DEVICE_KEY, 'nexo.installation.id', 'nexo.phase5.pos.draft']);
let initialized = false;
let initializing = false;
let internalStorageWrite = false;
let deferredInstallPrompt: any = null;
let syncInProgress = false;
let scheduleHandle: number | null = null;
let originalSetItem: typeof Storage.prototype.setItem | null = null;
let originalRemoveItem: typeof Storage.prototype.removeItem | null = null;
let cloudContextIdentity = '';

const nowIso = () => new Date().toISOString();
const uid = (prefix: string) => `${prefix}_${crypto.randomUUID?.() ?? `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`}`;

function simpleHash(value: string | null): string {
  const text = value ?? '__deleted__';
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

function isTrackedKey(key: string): boolean {
  return key.startsWith('nexo.') && !ignoredKeys.has(key) && !key.includes('.session');
}

function activeEditionId(): CommercialEditionId {
  const value = localStorage.getItem(EDITION_KEY) as CommercialEditionId | null;
  return value && value in commercialEditions ? value : 'trial';
}

function isSyncableKey(key: string): boolean {
  return isTrackedKey(key) && !localOnlyDatasets.has(key);
}

function isLocalOnlyEdition(): boolean {
  return activeEditionId() === 'local-perpetual';
}

function emitChange(): void {
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

async function metaValue<T>(key: string): Promise<T | undefined> {
  const record = await idbGet<MetaRecord>('meta', key);
  return record?.value as T | undefined;
}

async function setMeta(key: string, value: MetaRecord['value']): Promise<void> {
  await idbPut('meta', { key, value } satisfies MetaRecord);
}

function setLocalStorageSilently(key: string, value: string | null): void {
  internalStorageWrite = true;
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } finally {
    internalStorageWrite = false;
  }
  try {
    window.dispatchEvent(new StorageEvent('storage', { key, newValue: value, storageArea: localStorage, url: window.location.href }));
  } catch {
    window.dispatchEvent(new Event('storage'));
  }
}

async function currentRemoteRevision(key: string): Promise<number> {
  const remote = await idbGet<RemoteShadowRecord>('remoteShadow', key);
  return remote?.revision ?? 0;
}

async function captureDatasetChange(key: string, value: string | null, action: 'upsert' | 'delete'): Promise<void> {
  if (!initialized || internalStorageWrite || !isTrackedKey(key)) return;

  // Borrar el almacenamiento del navegador nunca debe borrar la copia de Supabase.
  // Se elimina solamente la caché local y se fuerza una lectura completa de la nube.
  if (action === 'delete') {
    await idbDelete('keyValue', key);
    if (!isLocalOnlyEdition() && isSyncableKey(key)) {
      await setMeta('lastSyncAt', null);
      emitChange();
      if (navigator.onLine) scheduleSync();
    } else {
      emitChange();
    }
    return;
  }

  const previous = await idbGet<LocalDatasetRecord>('keyValue', key);
  const hash = simpleHash(value);
  if (previous?.hash === hash) return;
  const timestamp = nowIso();
  const revision = (previous?.revision ?? 0) + 1;
  await idbPut('keyValue', { key, value: value ?? '', hash, revision, updatedAt: timestamp } satisfies LocalDatasetRecord);
  if (!isLocalOnlyEdition() && isSyncableKey(key)) {
    const queue = await idbGetAll<SyncOperation>('syncQueue');
    const existing = queue.find((item) => item.entityKey === key && ['pending', 'error'].includes(item.status));
    const baseRevision = existing?.baseRevision ?? await currentRemoteRevision(key);
    const operation: SyncOperation = {
      id: existing?.id ?? uid('sync'),
      entityKey: key,
      action: 'upsert',
      value,
      hash,
      baseRevision,
      clientRevision: revision,
      status: 'pending',
      retryCount: existing?.retryCount ?? 0,
      createdAt: existing?.createdAt ?? timestamp,
      updatedAt: timestamp,
    };
    await idbPut('syncQueue', operation);
  }
  emitChange();
  if (navigator.onLine && !isLocalOnlyEdition()) scheduleSync();
}

function patchStorage(): void {
  if (originalSetItem) return;
  originalSetItem = Storage.prototype.setItem;
  originalRemoveItem = Storage.prototype.removeItem;
  Storage.prototype.setItem = function setItem(key: string, value: string): void {
    originalSetItem!.call(this, key, value);
    if (this === window.localStorage && !internalStorageWrite) void captureDatasetChange(key, value, 'upsert');
  };
  Storage.prototype.removeItem = function removeItem(key: string): void {
    originalRemoveItem!.call(this, key);
    if (this === window.localStorage && !internalStorageWrite) void captureDatasetChange(key, null, 'delete');
  };
}

async function hydrateFromIndexedDb(): Promise<void> {
  const records = await idbGetAll<LocalDatasetRecord>('keyValue');
  for (const record of records) {
    if (localStorage.getItem(record.key) === null) setLocalStorageSilently(record.key, record.value);
  }
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (!key || !isTrackedKey(key)) continue;
    const value = localStorage.getItem(key) ?? '';
    const existing = await idbGet<LocalDatasetRecord>('keyValue', key);
    if (!existing) await idbPut('keyValue', { key, value, hash: simpleHash(value), revision: 1, updatedAt: nowIso() } satisfies LocalDatasetRecord);
  }
}

async function registerServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;

  const isLocalDevelopment = import.meta.env.DEV
    || ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);

  if (isLocalDevelopment) {
    // Un Service Worker de una compilación anterior puede seguir controlando localhost
    // y romper las rutas de Vite. En desarrollo siempre trabajamos sin Service Worker.
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));

    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames
          .filter((name) => name.startsWith('nexo-'))
          .map((name) => caches.delete(name)),
      );
    }
    return;
  }

  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/',
      updateViaCache: 'none',
    });
    await registration.update();
  } catch (error) {
    console.warn('[NEXO] No se pudo registrar el Service Worker.', error);
  }
}

async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  try { return await navigator.storage.persist(); } catch { return false; }
}

function scheduleSync(): void {
  if (scheduleHandle !== null) window.clearTimeout(scheduleHandle);
  scheduleHandle = window.setTimeout(() => { scheduleHandle = null; void synchronizeNow(); }, 250);
}

async function closeSupersededConflicts(entityKey: string): Promise<void> {
  const timestamp = nowIso();
  const conflicts = await idbGetAll<SyncConflict>('conflicts');
  for (const conflict of conflicts.filter((item) => item.entityKey === entityKey && item.status === 'open')) {
    await idbPut('conflicts', { ...conflict, status: 'resolved-local', resolvedAt: timestamp } satisfies SyncConflict);
  }
}

async function syncOperation(operation: SyncOperation): Promise<void> {
  const syncing: SyncOperation = { ...operation, status: 'syncing', updatedAt: nowIso(), error: undefined };
  await idbPut('syncQueue', syncing);
  if (cloudConfigured()) {
    const result = await pushCloudSync({
      id: operation.id,
      entityKey: operation.entityKey,
      action: operation.action,
      value: operation.value,
      hash: operation.hash,
      baseRevision: operation.baseRevision,
      clientRevision: operation.clientRevision,
      deviceId: localStorage.getItem(DEVICE_KEY),
      createdAt: operation.createdAt,
    });
    if (result.status === 'conflict') {
      const conflict: SyncConflict = {
        id: uid('conflict'), operationId: operation.id, entityKey: operation.entityKey,
        localValue: operation.value, syncedValue: result.value,
        localRevision: operation.clientRevision, syncedRevision: result.revision,
        detectedAt: nowIso(), status: 'open',
      };
      await idbPut('remoteShadow', { key: operation.entityKey, value: result.value, hash: result.hash, revision: result.revision, updatedAt: result.updatedAt } satisfies RemoteShadowRecord);
      await idbPut('conflicts', conflict);
      await idbPut('syncQueue', { ...syncing, status: 'conflict', updatedAt: nowIso(), error: 'Existe una versión sincronizada más reciente en la nube.' } satisfies SyncOperation);
      return;
    }
    await idbPut('remoteShadow', { key: operation.entityKey, value: result.value, hash: result.hash, revision: result.revision, updatedAt: result.updatedAt } satisfies RemoteShadowRecord);
    await idbPut('syncQueue', { ...syncing, status: 'synced', baseRevision: result.revision, syncedAt: nowIso(), updatedAt: nowIso() } satisfies SyncOperation);
    await closeSupersededConflicts(operation.entityKey);
    return;
  }
  const remote = await idbGet<RemoteShadowRecord>('remoteShadow', operation.entityKey);
  if (remote && remote.revision > operation.baseRevision && remote.hash !== operation.hash) {
    const conflict: SyncConflict = {
      id: uid('conflict'), operationId: operation.id, entityKey: operation.entityKey,
      localValue: operation.value, syncedValue: remote.value,
      localRevision: operation.clientRevision, syncedRevision: remote.revision,
      detectedAt: nowIso(), status: 'open',
    };
    await idbPut('conflicts', conflict);
    await idbPut('syncQueue', { ...syncing, status: 'conflict', updatedAt: nowIso(), error: 'Existe una versión sincronizada más reciente.' } satisfies SyncOperation);
    return;
  }
  const nextRevision = Math.max(remote?.revision ?? 0, operation.baseRevision) + 1;
  await idbPut('remoteShadow', { key: operation.entityKey, value: operation.value, hash: operation.hash, revision: nextRevision, updatedAt: nowIso() } satisfies RemoteShadowRecord);
  await idbPut('syncQueue', { ...syncing, status: 'synced', syncedAt: nowIso(), updatedAt: nowIso() } satisfies SyncOperation);
  await closeSupersededConflicts(operation.entityKey);
}

type RemoteDatasetChange = { entityKey: string; value: string | null; hash: string; revision: number; updatedAt: string };
const activeSyncStatuses: SyncOperationStatus[] = ['pending', 'syncing', 'error', 'conflict'];

async function preserveConflict(operation: SyncOperation, remote: RemoteDatasetChange): Promise<void> {
  if (operation.hash === remote.hash || operation.value === remote.value) {
    await idbPut('syncQueue', { ...operation, status: 'synced', baseRevision: remote.revision, syncedAt: nowIso(), updatedAt: nowIso(), error: undefined } satisfies SyncOperation);
    return;
  }
  const conflicts = await idbGetAll<SyncConflict>('conflicts');
  if (!conflicts.some((item) => item.operationId === operation.id && item.status === 'open')) {
    await idbPut('conflicts', {
      id: uid('conflict'),
      operationId: operation.id,
      entityKey: operation.entityKey,
      localValue: operation.value,
      syncedValue: remote.value,
      localRevision: operation.clientRevision,
      syncedRevision: remote.revision,
      detectedAt: nowIso(),
      status: 'open',
    } satisfies SyncConflict);
  }
  await idbPut('syncQueue', { ...operation, status: 'conflict', updatedAt: nowIso(), error: 'Supabase tiene una versión diferente. La copia remota se aplicó y la local quedó disponible para revisión.' } satisfies SyncOperation);
}

async function applyRemoteDataset(change: RemoteDatasetChange, pending?: SyncOperation): Promise<void> {
  await idbPut('remoteShadow', { key: change.entityKey, value: change.value, hash: change.hash, revision: change.revision, updatedAt: change.updatedAt } satisfies RemoteShadowRecord);
  if (pending) {
    const decision = decideDatasetReconciliation({ baseRevision: pending.baseRevision, remoteRevision: change.revision, localValue: pending.value, remoteValue: change.value, remoteExists: true });
    if (decision === 'keep-local') {
      const timestamp = nowIso();
      setLocalStorageSilently(pending.entityKey, pending.value);
      if (pending.value === null) await idbDelete('keyValue', pending.entityKey);
      else await idbPut('keyValue', { key: pending.entityKey, value: pending.value, hash: pending.hash, revision: pending.clientRevision, updatedAt: timestamp } satisfies LocalDatasetRecord);
      await idbPut('syncQueue', { ...pending, status: 'pending', baseRevision: change.revision, error: undefined, updatedAt: timestamp } satisfies SyncOperation);
      return;
    }
    await preserveConflict(pending, change);
  }
  setLocalStorageSilently(change.entityKey, change.value);
  if (change.value === null) await idbDelete('keyValue', change.entityKey);
  else await idbPut('keyValue', { key: change.entityKey, value: change.value, hash: change.hash, revision: change.revision, updatedAt: change.updatedAt } satisfies LocalDatasetRecord);
  if (change.entityKey === CATALOG_KEY && change.value) {
    try {
      const businessId = getAuthState().workspace?.activeContext?.businessId;
      const database = JSON.parse(change.value) as { catalogs?: Record<string, Record<string, unknown>> };
      const catalog = businessId ? database.catalogs?.[businessId] : undefined;
      if (catalog) await syncCatalogSnapshot(catalog);
    } catch (error) {
      console.warn('[NEXO] El catálogo sincronizado no pudo publicarse todavía.', error);
    }
  }
}

async function pullAndApplyCloud(since?: string, authoritative = false): Promise<void> {
  const remote = await pullCloudSync(since);
  const queue = await idbGetAll<SyncOperation>('syncQueue');
  const pendingByKey = new Map(queue.filter((item) => activeSyncStatuses.includes(item.status)).sort((a, b) => a.updatedAt.localeCompare(b.updatedAt)).map((item) => [item.entityKey, item]));
  const remoteKeys = new Set<string>();

  for (const change of remote.changes) {
    remoteKeys.add(change.entityKey);
    await applyRemoteDataset(change, pendingByKey.get(change.entityKey));
  }

  if (authoritative) {
    const cached = await idbGetAll<LocalDatasetRecord>('keyValue');
    const localKeys = new Set(cached.map((item) => item.key));
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key && isSyncableKey(key)) localKeys.add(key);
    }
    for (const key of localKeys) {
      if (!isSyncableKey(key) || remoteKeys.has(key)) continue;
      const remoteMissing: RemoteDatasetChange = { entityKey: key, value: null, hash: simpleHash(null), revision: 0, updatedAt: remote.serverTime };
      const pending = pendingByKey.get(key);
      if (pending && decideDatasetReconciliation({ baseRevision: pending.baseRevision, remoteRevision: 0, localValue: pending.value, remoteValue: null, remoteExists: false }) === 'keep-local') {
        await idbDelete('remoteShadow', key);
        continue;
      }
      if (pending) await preserveConflict(pending, remoteMissing);
      setLocalStorageSilently(key, null);
      await idbDelete('keyValue', key);
      await idbDelete('remoteShadow', key);
    }
  }

  await setMeta('lastSyncAt', remote.serverTime);
}

export async function synchronizeNow(): Promise<void> {
  if (syncInProgress || !initialized || !navigator.onLine || isLocalOnlyEdition()) return;
  if (cloudConfigured() && !getAuthState().session) return;
  syncInProgress = true;
  emitChange();
  try {
    // Supabase se consulta antes de enviar cambios. Una versión remota más reciente
    // nunca es reemplazada silenciosamente por una copia antigua del navegador.
    if (cloudConfigured()) {
      // Son pocas colecciones por organización. Una lectura completa permite detectar
      // también las colecciones eliminadas directamente en Supabase (sin tombstone).
      await pullAndApplyCloud(undefined, true);
    }

    const operations = (await idbGetAll<SyncOperation>('syncQueue'))
      .filter((item) => ['pending', 'error'].includes(item.status))
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    for (const operation of operations) {
      try { await syncOperation(operation); }
      catch (error) {
        await idbPut('syncQueue', { ...operation, status: 'error', retryCount: operation.retryCount + 1, error: error instanceof Error ? error.message : 'Error desconocido', updatedAt: nowIso() } satisfies SyncOperation);
      }
    }
    if (!cloudConfigured()) await setMeta('lastSyncAt', nowIso());
  } finally {
    syncInProgress = false;
    emitChange();
  }
}

export async function retryFailedOperations(): Promise<void> {
  const operations = await idbGetAll<SyncOperation>('syncQueue');
  for (const operation of operations.filter((item) => item.status === 'error')) {
    await idbPut('syncQueue', { ...operation, status: 'pending', error: undefined, updatedAt: nowIso() } satisfies SyncOperation);
  }
  emitChange();
  await synchronizeNow();
}

export async function resolveSyncConflict(conflictId: string, resolution: ConflictResolution): Promise<void> {
  const conflict = await idbGet<SyncConflict>('conflicts', conflictId);
  if (!conflict || conflict.status !== 'open') throw new Error('El conflicto ya no está disponible.');
  const operation = await idbGet<SyncOperation>('syncQueue', conflict.operationId);
  if (!operation) throw new Error('No se encontró la operación relacionada.');
  const timestamp = nowIso();
  if (resolution === 'keep-local') {
    const remote = await idbGet<RemoteShadowRecord>('remoteShadow', conflict.entityKey);
    await idbPut('syncQueue', { ...operation, status: 'pending', baseRevision: remote?.revision ?? 0, error: undefined, updatedAt: timestamp } satisfies SyncOperation);
    await idbPut('conflicts', { ...conflict, status: 'resolved-local', resolvedAt: timestamp } satisfies SyncConflict);
    emitChange();
    await synchronizeNow();
    return;
  }
  setLocalStorageSilently(conflict.entityKey, conflict.syncedValue);
  if (conflict.syncedValue === null) await idbDelete('keyValue', conflict.entityKey);
  else await idbPut('keyValue', { key: conflict.entityKey, value: conflict.syncedValue, hash: simpleHash(conflict.syncedValue), revision: conflict.syncedRevision, updatedAt: timestamp } satisfies LocalDatasetRecord);
  await idbPut('syncQueue', { ...operation, status: 'synced', value: conflict.syncedValue, hash: simpleHash(conflict.syncedValue), syncedAt: timestamp, updatedAt: timestamp, error: undefined } satisfies SyncOperation);
  await idbPut('conflicts', { ...conflict, status: 'resolved-synced', resolvedAt: timestamp } satisfies SyncConflict);
  emitChange();
  window.location.reload();
}

export async function createValidationConflict(): Promise<void> {
  if (isLocalOnlyEdition()) throw new Error('La licencia local permanente no utiliza sincronización en la nube.');
  const key = 'nexo.phase11.validation.conflict';
  const remoteValue = JSON.stringify({ source: 'synced-copy', updatedAt: nowIso() });
  const localValue = JSON.stringify({ source: 'this-device', updatedAt: nowIso() });
  await idbPut('remoteShadow', { key, value: remoteValue, hash: simpleHash(remoteValue), revision: 10, updatedAt: nowIso() } satisfies RemoteShadowRecord);
  setLocalStorageSilently(key, localValue);
  await idbPut('keyValue', { key, value: localValue, hash: simpleHash(localValue), revision: 2, updatedAt: nowIso() } satisfies LocalDatasetRecord);
  await idbPut('syncQueue', { id: uid('sync'), entityKey: key, action: 'upsert', value: localValue, hash: simpleHash(localValue), baseRevision: 1, clientRevision: 2, status: 'pending', retryCount: 0, createdAt: nowIso(), updatedAt: nowIso() } satisfies SyncOperation);
  emitChange();
  await synchronizeNow();
}

export function createOfflineValidationChange(): void {
  localStorage.setItem(VALIDATION_KEY, JSON.stringify({ createdAt: nowIso(), deviceId: localStorage.getItem(DEVICE_KEY) }));
}

export async function clearSyncedHistory(): Promise<void> {
  const operations = await idbGetAll<SyncOperation>('syncQueue');
  for (const operation of operations.filter((item) => item.status === 'synced')) await idbDelete('syncQueue', operation.id);
  emitChange();
}

export async function exportLocalBackup(): Promise<void> {
  const datasets = await idbGetAll<LocalDatasetRecord>('keyValue');
  const backup = {
    format: 'nexo-local-backup-v1',
    generatedAt: nowIso(),
    editionId: activeEditionId(),
    deviceId: localStorage.getItem(DEVICE_KEY),
    datasets: datasets.map(({ key, value }) => ({ key, value })),
  };
  const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `nexo-respaldo-${new Date().toISOString().slice(0, 10)}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function importLocalBackup(file: File): Promise<void> {
  const parsed = JSON.parse(await file.text()) as { format?: string; datasets?: { key: string; value: string }[] };
  if (parsed.format !== 'nexo-local-backup-v1' || !Array.isArray(parsed.datasets)) throw new Error('El archivo no es un respaldo válido de NEXO.');
  for (const dataset of parsed.datasets) {
    if (!isTrackedKey(dataset.key) || typeof dataset.value !== 'string') continue;
    setLocalStorageSilently(dataset.key, dataset.value);
    await idbPut('keyValue', { key: dataset.key, value: dataset.value, hash: simpleHash(dataset.value), revision: 1, updatedAt: nowIso() } satisfies LocalDatasetRecord);
  }
  emitChange();
  window.location.reload();
}

export async function installPwa(): Promise<boolean> {
  if (!deferredInstallPrompt) return false;
  await deferredInstallPrompt.prompt();
  const result = await deferredInstallPrompt.userChoice;
  deferredInstallPrompt = null;
  emitChange();
  return result?.outcome === 'accepted';
}

export async function getOfflineState(): Promise<OfflineState> {
  const editionId = activeEditionId();
  const edition = commercialEditions[editionId];
  const queue = initialized ? await idbGetAll<SyncOperation>('syncQueue') : [];
  const conflicts = initialized ? await idbGetAll<SyncConflict>('conflicts') : [];
  const datasets = initialized ? await idbGetAll<LocalDatasetRecord>('keyValue') : [];
  const estimate = await navigator.storage?.estimate?.().catch(() => undefined);
  const persistent = await navigator.storage?.persisted?.().catch(() => false) ?? false;
  const serviceWorkerReady = 'serviceWorker' in navigator ? Boolean(await navigator.serviceWorker.getRegistration('/').catch(() => undefined)) : false;
  return {
    ready: initialized,
    connection: editionId === 'local-perpetual' ? 'local-only' : navigator.onLine ? 'online' : 'offline',
    editionId,
    editionName: edition.name,
    indexedDbAvailable: 'indexedDB' in window,
    serviceWorkerReady,
    installAvailable: Boolean(deferredInstallPrompt),
    persistentStorage: persistent,
    storageUsedBytes: estimate?.usage ?? 0,
    storageQuotaBytes: estimate?.quota ?? 0,
    trackedDatasets: datasets.length,
    pendingOperations: queue.filter((item) => ['pending', 'syncing'].includes(item.status)).length,
    syncedOperations: queue.filter((item) => item.status === 'synced').length,
    errorOperations: queue.filter((item) => item.status === 'error').length,
    openConflicts: conflicts.filter((item) => item.status === 'open').length,
    lastSyncAt: await metaValue<string>('lastSyncAt'),
    queue: queue.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    conflicts: conflicts.sort((a, b) => b.detectedAt.localeCompare(a.detectedAt)),
  };
}

export function subscribeOfflineState(listener: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, listener);
  window.addEventListener('online', listener);
  window.addEventListener('offline', listener);
  return () => {
    window.removeEventListener(CHANGE_EVENT, listener);
    window.removeEventListener('online', listener);
    window.removeEventListener('offline', listener);
  };
}

function currentCloudIdentity(): string {
  const auth = getAuthState();
  const organizationId = auth.workspace?.activeContext?.organizationId;
  return auth.session && organizationId ? `${auth.session.user.id}:${organizationId}` : '';
}

async function bootstrapCloudContext(): Promise<boolean> {
  const identity = currentCloudIdentity();
  if (!cloudConfigured() || !identity || !navigator.onLine || isLocalOnlyEdition()) return false;
  if (identity === cloudContextIdentity) return true;
  await pullAndApplyCloud(undefined, true);
  cloudContextIdentity = identity;
  return true;
}

export async function initializeOfflineEngine(): Promise<void> {
  if (initializing) return;
  if (initialized) {
    const identity = currentCloudIdentity();
    if (identity && identity !== cloudContextIdentity) {
      initializing = true;
      try { await bootstrapCloudContext(); }
      finally { initializing = false; emitChange(); }
    }
    return;
  }
  const opaqueOrigin = window.location.protocol === 'about:' || window.location.protocol === 'data:' || window.location.origin === 'null';
  if (opaqueOrigin) return;
  initializing = true;
  try {
    await openOfflineDatabase();
    if (!localStorage.getItem(DEVICE_KEY)) setLocalStorageSilently(DEVICE_KEY, uid('device'));

    let cloudHydrated = false;
    try { cloudHydrated = await bootstrapCloudContext(); }
    catch (error) { console.warn('[NEXO] No se pudo iniciar desde Supabase; se usará la copia local hasta recuperar conexión.', error); }
    if (!cloudHydrated) await hydrateFromIndexedDb();

    patchStorage();
    await registerServiceWorker();
    await requestPersistentStorage();
    initialized = true;
    window.addEventListener('online', () => { emitChange(); void synchronizeNow(); });
    window.addEventListener('offline', emitChange);
    window.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault();
      deferredInstallPrompt = event;
      emitChange();
    });
    window.addEventListener('appinstalled', () => { deferredInstallPrompt = null; emitChange(); });
    emitChange();
    if (navigator.onLine && !isLocalOnlyEdition()) await synchronizeNow();
  } catch {
    initialized = false;
    emitChange();
  } finally {
    initializing = false;
  }
}

export async function resetOfflineValidationData(): Promise<void> {
  await idbClear('syncQueue');
  await idbClear('conflicts');
  await idbDelete('keyValue', VALIDATION_KEY);
  await idbDelete('keyValue', 'nexo.phase11.validation.conflict');
  setLocalStorageSilently(VALIDATION_KEY, null);
  setLocalStorageSilently('nexo.phase11.validation.conflict', null);
  emitChange();
}
